import { useEffect, useRef, useState } from 'react'

interface PillListCellProps {
  items: string[]
  maxColumnWidth?: number
  maxPillWidth?: number
  maxVisibleItems?: number
  totalCount?: number // Optional total count for "+X more" calculation
}

export const PillListCell = ({
  items,
  maxColumnWidth = 250,
  maxPillWidth = 150,
  maxVisibleItems,
  totalCount, // specifies a total if only a subset of `items` is serialized and passed in
}: PillListCellProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(items.length)

  useEffect(() => {
    if (!containerRef.current || maxVisibleItems !== undefined) {
      if (maxVisibleItems !== undefined) {
        setVisibleCount(Math.min(maxVisibleItems, items.length))
      }
      return
    }

    // Measure actual pill widths
    const pills = containerRef.current.querySelectorAll('.pill-item')
    if (pills.length === 0) return

    const pillGap = 8 // gap-2 = 8px
    const moreIndicatorWidth = 60 // estimated width for "+X more"

    let accumulatedWidth = 0
    let count = 0

    for (let i = 0; i < pills.length; i++) {
      const pill = pills[i] as HTMLElement
      const pillWidth = pill.offsetWidth
      const gapWidth = i > 0 ? pillGap : 0

      const totalWithPill = accumulatedWidth + gapWidth + pillWidth

      // Check if this is the last item
      if (i === pills.length - 1) {
        // Last item - just check if it fits
        if (totalWithPill <= maxColumnWidth) {
          count++
        }
      } else {
        // Not last - check if pill + indicator fits
        const withIndicator = totalWithPill + pillGap + moreIndicatorWidth
        if (withIndicator <= maxColumnWidth) {
          accumulatedWidth = totalWithPill
          count++
        } else {
          break
        }
      }
    }

    setVisibleCount(count)
  }, [items, maxColumnWidth, maxVisibleItems])

  if (items.length === 0) return <>-</>

  // Initial render: show all items for measurement
  const showAll = visibleCount === items.length

  return (
    <div
      ref={containerRef}
      className="flex flex-wrap gap-2 items-center"
      style={{ maxWidth: `${maxColumnWidth}px` }}
    >
      {items
        .slice(0, showAll ? items.length : visibleCount)
        .map((item, index) => (
          <span
            key={index}
            className="pill-item inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 truncate"
            style={{ maxWidth: `${maxPillWidth}px` }}
            title={item}
          >
            {item}
          </span>
        ))}
      {visibleCount < (totalCount ?? items.length) && (
        <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-500">
          +{(totalCount ?? items.length) - visibleCount}
        </span>
      )}
    </div>
  )
}
